import { z } from 'zod';

// Selectable colour themes and dark mode (ADR 0030). A theme is a block of colour token
// overrides in packages/ui/src/tokens.css plus one entry here; tokens.test.ts fails if the
// two lists differ, and checks every theme's contrast in light and dark.

/** The themes people can choose. The first is the built-in default. */
export const THEMES = [
  {
    id: 'medical-blue',
    label: 'Medical blue',
    description: 'Blue for actions, teal when done, amber when waiting.',
  },
  {
    id: 'teal',
    label: 'Teal',
    description: 'Deep teal for actions, green when done, amber when waiting.',
  },
  {
    id: 'neutral',
    label: 'Neutral',
    description: 'Black and white; states are told by words and shapes.',
  },
] as const;

export type ThemeId = (typeof THEMES)[number]['id'];

export const THEME_IDS = THEMES.map((t) => t.id) as [ThemeId, ...ThemeId[]];

export const DEFAULT_THEME: ThemeId = 'medical-blue';

export const themeIdSchema = z.enum(THEME_IDS);

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && (THEME_IDS as readonly string[]).includes(value);
}

/** A stored theme ID, or the built-in default when it is no longer on the list. */
export function themeOrDefault(value: string | null | undefined): ThemeId {
  return isThemeId(value) ? value : DEFAULT_THEME;
}

/** A person's chosen mode. Not choosing one (null) means "Match my device". */
export const APPEARANCE_MODES = ['light', 'dark'] as const;
export type AppearanceMode = (typeof APPEARANCE_MODES)[number];
export const appearanceModeSchema = z.enum(APPEARANCE_MODES);

/** The mode in effect: light, dark, or follow the device's `prefers-color-scheme`. */
export type EffectiveMode = AppearanceMode | 'system';
export const effectiveModeSchema = z.enum(['light', 'dark', 'system']);

/** What the app applies at start: the session carries it (GET /auth/session). */
export const effectiveAppearanceSchema = z.object({
  theme: themeIdSchema,
  mode: effectiveModeSchema,
});
export type EffectiveAppearance = z.infer<typeof effectiveAppearanceSchema>;

/**
 * The signed-in person's own appearance (GET and PUT /me/appearance). A null theme uses the
 * facility's default; a null mode matches the device.
 */
export const appearanceSchema = z.object({
  theme: themeIdSchema.nullable(),
  mode: appearanceModeSchema.nullable(),
  facilityDefaultTheme: themeIdSchema,
  effective: effectiveAppearanceSchema,
});
export type AppearanceView = z.infer<typeof appearanceSchema>;

export const updateAppearanceBodySchema = z.object({
  theme: themeIdSchema.nullable(),
  mode: appearanceModeSchema.nullable(),
});
export type UpdateAppearanceBody = z.infer<typeof updateAppearanceBodySchema>;

/** PUT /facilities/:facilityId/default-theme (admin.facilities.manage). */
export const setDefaultThemeBodySchema = z.object({ theme: themeIdSchema });

/**
 * The theme and mode in effect. A stored value that is no longer a theme (one was removed)
 * falls back to the facility default, then the built-in default.
 */
export function effectiveAppearance(
  user: { theme: string | null; mode: string | null },
  facilityDefaultTheme: string | null,
): EffectiveAppearance {
  const theme = isThemeId(user.theme)
    ? user.theme
    : isThemeId(facilityDefaultTheme)
      ? facilityDefaultTheme
      : DEFAULT_THEME;
  const mode = user.mode === 'light' || user.mode === 'dark' ? user.mode : 'system';
  return { theme, mode };
}
